import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { Formik } from 'formik';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  IconButton,
  Grid,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  calculateFirstRentDateFromGracePeriod,
  calculateEndDateFromGracePeriod
} from 'src/utils/contracts';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { selectDistributorDetail } from 'src/store/selectors/distributorSelectors';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import { UPDATE_CONTRACT_DATES } from 'src/store/actions/contractActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  getGracePeriodFromMonthDiff,
  getMaxDateForSecondRentDate
} from 'src/utils/contract';
import getMonthDiff from 'src/utils/date';
import useStyles from './styles';
import validationSchema from './schema';
import makeInitialState from './initialState';
import DialogTransition from '../../../../../components/DialogTransition';
import Loading from '../../../../../components/Loading';

const EditContractDateDialog = ({ open, contract, handleClose, submit }) => {
  const parameter = useSelector(selectDistributorDetail);
  const loading = useSelector(
    isLoadingSelector([UPDATE_CONTRACT_DATES.action])
  );
  const initialState = useMemo(() => makeInitialState(contract), [
    contract,
    parameter
  ]);
  const { classes } = useStyles();
  const resetRef = useRef();

  const { t } = useTranslation();

  const calculateGracePeriod = useCallback(
    (startDate, firstRentDate, isFirstUpfrontRentModality) => {
      if (isFirstUpfrontRentModality) {
        const secondRentDate = firstRentDate.clone().add(1, 'month');
        const rawMonthDiff = getMonthDiff(startDate, secondRentDate);
        return getGracePeriodFromMonthDiff(rawMonthDiff);
      }
      const rawMonthDiff = getMonthDiff(startDate, firstRentDate);
      return getGracePeriodFromMonthDiff(rawMonthDiff);
    },
    [getMonthDiff, getGracePeriodFromMonthDiff]
  );

  const handleStartDateChange = useCallback(
    (newValue, setFieldValue, values) => {
      setFieldValue('startDate', momentLocalTimezone(newValue));

      const firstRentDate = calculateFirstRentDateFromGracePeriod(
        momentLocalTimezone(newValue),
        values.gracePeriod,
        contract.isFirstUpfrontRentModality,
        contract.initialPayment
      );

      setFieldValue('firstRentDate', firstRentDate);

      if (contract.isFirstUpfrontRentModality) {
        setFieldValue('secondRentDate', firstRentDate.clone().add(1, 'month'));
      }

      setFieldValue(
        'endDate',
        calculateEndDateFromGracePeriod(
          momentLocalTimezone(newValue),
          contract.term,
          values.gracePeriod,
          contract.isFirstUpfrontRentModality
        )
      );
    },
    [
      calculateFirstRentDateFromGracePeriod,
      calculateEndDateFromGracePeriod,
      contract
    ]
  );

  const handleOuterSubmit = useCallback(
    (values, { resetForm }) => {
      submit(values);
      resetForm(initialState);
    },
    [submit, initialState, contract.id]
  );

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.root }}
      open={open}
      TransitionComponent={DialogTransition}
      keepMounted
    >
      <DialogTitle className={classes.header} id="form-address-dialog-title">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            handleClose();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {contract.isMsiModality
            ? t('CONTRACT_VIEW.CONTRACT_DETAIL.EDIT_CONTRACT_DATES_MSI')
            : t('CONTRACT_VIEW.CONTRACT_DETAIL.EDIT_CONTRACT_DATES')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ marginBottom: '16px' }}>
        {loading ? (
          <Loading />
        ) : (
          <Formik
            initialValues={initialState}
            validationSchema={validationSchema}
            onSubmit={handleOuterSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              touched,
              values,
              resetForm
            }) => {
              resetRef.current = () => resetForm(initialState);
              return (
                <form onSubmit={handleSubmit}>
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(
                      touched.firstRentDate && errors.firstRentDate
                    )}
                    fullWidth
                    helperText={
                      touched.firstRentDate && t(errors.firstRentDate)
                    }
                    label={t('CONTRACT_VIEW.PAY_DAY')}
                    margin="normal"
                    disabled
                    name="firstRentDate"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={selectFormatDate(
                      values.firstRentDate,
                      dateFormatType.ONLY_DAY
                    )}
                    variant="outlined"
                  />
                  <DatePicker
                    label={t('CONTRACT_VIEW.HOOK_DATE')}
                    format="DD/MM/YYYY"
                    value={values.startDate}
                    onChange={newValue => {
                      handleStartDateChange(newValue, setFieldValue, values);
                    }}
                    slotProps={{
                      textField: {
                        sx: { width: '100%', margin: '16px 0' },
                        error: Boolean(touched.startDate && errors.startDate),
                        helperText: touched.startDate && t(errors.startDate),
                        FormHelperTextProps: { sx: { color: 'red' } },
                        InputProps: { autoComplete: 'off' }
                      }
                    }}
                  />

                  {!contract.isFirstUpfrontRentModality && (
                    <DatePicker
                      label={t('CONTRACT_VIEW.FIRST_RENT_DATE')}
                      value={values.firstRentDate}
                      format="DD/MM/YYYY"
                      disabled={contract.isMsiModality}
                      onChange={newValue => {
                        setFieldValue(
                          'firstRentDate',
                          momentLocalTimezone(newValue)
                        );
                        const gracePeriod = calculateGracePeriod(
                          values.startDate,
                          momentLocalTimezone(newValue),
                          false
                        );
                        setFieldValue('gracePeriod', gracePeriod);
                        setFieldValue(
                          'endDate',
                          momentLocalTimezone(newValue).add(
                            contract.term - 1,
                            'months'
                          )
                        );
                      }}
                      slotProps={{
                        textField: {
                          sx: { width: '100%', margin: '16px 0' },
                          error: Boolean(touched.firstRentDate && errors.firstRentDate),
                          helperText: touched.firstRentDate && t(errors.firstRentDate),
                          FormHelperTextProps: { sx: { color: 'red' } },
                          InputProps: { autoComplete: 'off' }
                        }
                      }}
                    />
                  )}

                  {contract.isFirstUpfrontRentModality && (
                    <DatePicker
                      label={t('CONTRACT_VIEW.SECOND_RENT_DATE')}
                      value={values.secondRentDate}
                      format="DD/MM/YYYY"
                      maxDate={getMaxDateForSecondRentDate(
                        parameter.maxGracePeriod,
                        values.startDate
                      )}
                      minDate={momentLocalTimezone(values.startDate).add(
                        2,
                        'months'
                      )}
                      onChange={newValue => {
                        const updatedValue = momentLocalTimezone(newValue);

                        const secondRentDate = updatedValue.clone();

                        const firstRentDate = updatedValue
                          .clone()
                          .subtract(2, 'month');

                        const endDate = updatedValue
                          .clone()
                          .add(contract.term - 2, 'months');

                        setFieldValue('secondRentDate', secondRentDate);

                        setFieldValue('firstRentDate', firstRentDate);

                        const gracePeriod = calculateGracePeriod(
                          values.startDate,
                          firstRentDate,
                          true
                        );
                        setFieldValue('gracePeriod', gracePeriod);

                        setFieldValue('endDate', endDate);
                      }}
                      slotProps={{
                        textField: {
                          sx: { width: '100%', margin: '16px 0' },
                          error: Boolean(touched.secondRentDate && errors.secondRentDate),
                          helperText: touched.secondRentDate && t(errors.secondRentDate),
                          FormHelperTextProps: { sx: { color: 'red' } },
                          InputProps: { autoComplete: 'off' }
                        }
                      }}
                    />
                  )}

                  <DatePicker
                    label={t('CONTRACT_VIEW.LAST_RENT_DATE')}
                    value={values.endDate}
                    format="DD/MM/YYYY"
                    disabled
                    setValue={value => {
                      setFieldValue(
                        'endDate',
                        calculateFirstRentDateFromGracePeriod(
                          momentLocalTimezone(values.startDate),
                          value,
                          values.isFirstUpfrontRentModality,
                          Number(values.initialPayment)
                        ).add(values.term - 1, 'months')
                      );
                    }}
                    slotProps={{
                      textField: {
                        sx: { width: '100%', margin: '16px 0' },
                        error: Boolean(touched.endDate && errors.endDate),
                        helperText: touched.endDate && t(errors.endDate),
                        FormHelperTextProps: { sx: { color: 'red' } },
                        InputProps: { autoComplete: 'off' }
                      }
                    }}
                  />
                  <Grid container direction="column" alignItems="stretch">
                    <Grid item className={classes.buttons}>
                      <Button
                        className={clsx(classes.label, classes.buttons)}
                        type="submit"
                        color="primary"
                        variant="outlined"
                      >
                        {t('COMMON.SAVE')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

EditContractDateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  contract: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired
};

export default EditContractDateDialog;
